/* 楊修（字德祖） */
import { SG, landing, spMul } from '../../core/kit.ts';
import type { Unit, World } from '../types.ts';
import { defineHero, hook, P, S, D } from '../define.ts';

// 謎題牌：每 0.5 秒輪一張
const CARDS = ['blue', 'red', 'gold'];
SG.CARD_NAME = { blue: '一合酥', red: '闊門', gold: '雞肋' };
const cardIdx = (w: World, u: Unit) => Math.floor((w.t - u.cardSel.t0) / 0.5) % 3;
SG.cardShown = (w: World, u: Unit) => (u.cardSel ? CARDS[cardIdx(w, u)] : null);

export default defineHero({
  id: 'yangxiu', name: '楊修', sub: '字德祖', faction: 'wei', role: '法師', weapon: 'fan',
  look: { wart: 'cards', weaponName: '謎題牌', cards: true, head: 'guan', robe: true, skin: '#f3dcc4', eye: 'narrow', fx: '#ffd46b', c: { cloth: '#5a3a6a', sleeve: '#5a3a6a', trim: '#e9c46a', fan: '#f0e6c8' } },
  skills: [
    ['三策', 'bolt', '朝方向扇形擲出三張字條，各自穿透敵人，造成 {60/105/150/195} 傷害。', { x: { k: 'line', n: 3, fan: 0.26, dmg: [60, 105, 150, 195], rad: 24, speed: 900, shape: 'card', fromCast: true }, aim: D(720, 24), cd: [8, 7.5, 7, 6.5], mp: [60, 70, 80, 90], el: null }],
    ['解謎', 'buff', '抽牌：三張謎題牌每 0.5 秒輪流亮起，4 秒內再按一次鎖定（逾時自動停在當下那張），下一次普攻打出這張牌。一合酥（青）：{40/60/80/100} 傷害、回復 {50/75/100/125} 氣，周圍友軍各回同量生命。闊門（紅）：範圍 {60/90/120/150} 傷害並緩速 {30/35/40/45}%。雞肋（金）：{50/75/100/125} 傷害並暈眩 {0.8/1/1.2/1.4} 秒。', { x: { k: 'card', blue: [40, 60, 80, 100], mana: [50, 75, 100, 125], red: [60, 90, 120, 150], slow: [30, 35, 40, 45], gold: [50, 75, 100, 125], stun: [0.8, 1, 1.2, 1.4] }, aim: S(0), recast: 4, cd: [8, 7, 6, 5], mp: [40, 50, 60, 70], el: null }],
    ['恃才', 'passive', '攻速 +{5/10/15/20}%；每第 4 次普攻追加 {40/55/70/85} 傷害。', { pv: { asp: [5, 10, 15, 20] }, sig: 'stack4', bonus: [40, 55, 70, 85], stack: 'st4', max: 3 }],
    ['揣度上意', 'command', '看穿全局：全地圖敵將現形 {6/8/10} 秒（無視迷霧與隱身）；期間再按一次 R，引導 1.5 秒後傳送到指定地點。', { x: { k: 'destiny', dur: [6, 8, 10] }, aim: P(6000, 0), recast: 10, cd: [120, 100, 80], mp: [150, 175, 200], el: null, shout: '丞相之意，修已盡知。' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    card(u, a, X) {
      if (u.cardSel) { this.lockCard(u); return; }
      if (u.card && this.t - u.card.t0 < 1.5) return; // 剛鎖定（第二次按下的出手動作）
      u.card = null; u.cardSel = { t0: this.t, X };
      this.tell(u, '解謎：再按一次鎖定謎題牌', 'info');
    },
    destiny(u, a, X) {
      if (u.destinyOpen && this.t < u.destinyOpen) { // 第二次：引導後傳送
        u.destinyOpen = 0;
        if (!u.human) return; // 電腦不會用來傳送
        const dest = a.pt;
        u.st.root = Math.max(u.st.root, 1.5);
        this.fx({ k: 'warn', x: dest[0], y: dest[1], r: 70, t: 1.5 });
        this.later(1.5, () => {
          if (!u.alive || u.st.stun > 0 || u.st.knockup > 0 || u.st.freeze > 0 || u.st.silence > 0) { this.tell(u, '傳送被打斷', 'warn'); return; }
          const q = landing(u.x, u.y, dest[0], dest[1]); // 目的地不能站就往回找最近能站的點
          this.fx({ k: 'puff', x: u.x, y: u.y }); u.x = q[0]; u.y = q[1]; u.act = null; u.input.moveTo = null; this.fx({ k: 'puff', x: u.x, y: u.y });
        });
        return;
      }
      u.destinyOpen = this.t + (a.sk.recast || 10);
      this.units.forEach((t) => { if (t.kind === 'hero' && t.alive && t.team === 1 - u.team) this.expose(t, u.team, X.dur); });
      this.say(`${u.name}：「${a.sk.shout}」敵將行蹤盡現！`, 'big');
    },
  },
  methods: {
    lockCard(u) {
      const type = CARDS[cardIdx(this, u)];
      u.card = { type, X: u.cardSel.X, t0: this.t, until: this.t + 8 }; u.cardSel = null;
      this.tell(u, `鎖定「${SG.CARD_NAME[type]}」：下一次普攻打出`, 'info');
      this.fx({ k: 'spark', x: u.x, y: u.y - 120, col: type === 'blue' ? '#7fd8ff' : type === 'red' ? '#ff7a5a' : '#ffd46b' });
    },
    throwCard(u, t, a) {
      const c = u.card, X = c.X, sp = spMul(u);
      u.card = null;
      const basic = this.xCrit(u, this.atkPower(u), a);
      const col = c.type === 'blue' ? '#7fd8ff' : c.type === 'red' ? '#ff7a5a' : '#ffd46b';
      this.shootProj(u, { homing: t, speed: 950, shape: 'card', card: c.type, col, el: null,
        onHit: (o: Unit) => {
          this.damage(u, o, basic, { basic: true, stagger: 0.15 });
          this.onHitProcs(u, o);
          if (c.type === 'blue') {
            this.damage(u, o, X.blue * sp, { magic: true, spell: true });
            u.mp = Math.min(u.maxMp, u.mp + X.mana);
            this.alliesNear(u, 400).forEach((h) => { if (h.kind === 'hero') this.heal(h, X.mana); });
            this.fx({ k: 'heal', x: u.x, y: u.y, col });
          } else if (c.type === 'red') {
            this.enemiesNear(u, o.x, o.y, 170).forEach((e) => { if (e.structure) return; this.damage(u, e, X.red * sp, { magic: true, spell: true, stagger: 0.2 }); this.applyCC(u, e, { slow: [X.slow / 100, 2.5] }); });
            this.fx({ k: 'slam', x: o.x, y: o.y, col });
          } else {
            this.damage(u, o, X.gold * sp, { magic: true, spell: true });
            this.applyCC(u, o, { stun: X.stun });
            this.fx({ k: 'thunder', x: o.x, y: o.y, col });
          }
          if (u.human || o.human) this.dmgEvents.push({ x: o.x, y: o.y - 70, text: SG.CARD_NAME[c.type], team: o.team, src: u.id, tgt: o.id });
        } });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 解謎：鎖定的謎題牌由下一次普攻擲出
    hook('attack', 70, function (u, a, next) { const t = a.target; if (u.card && t && t.alive && t.team !== u.team && !a.noHit) { this.throwCard(u, t, a); return; } return next(); }),
    // 解謎：按下第二次的那一刻就鎖定當下亮著的牌（不等出手動作），出手動作只是演出
    hook('cast.pre', 10, function (u, i, x, y) { const sk = u.spec.skills && u.spec.skills[i]; if (u.cardSel && sk && sk.x && sk.x.k === 'card' && u.rc && u.rc[i] && this.t < u.rc[i].until) this.lockCard(u); }),
    // 普攻是擲出的字條
    hook('proj', 10, function (u, o, next) { if (o.basic && u.spec && u.spec.cards) o = Object.assign({}, o, { shape: 'card' }); return next(u, o); }),
    // 恃才：每第 4 次普攻追加傷害
    hook('onHit', 10, function (u, t) {
      const s = u.sg && u.sg.stack4;
      if (s && t.alive && !t.structure && (u.st4 = (u.st4 || 0) + 1) >= 4) {
        u.st4 = 0;
        this.damage(u, t, s.v('bonus') * spMul(u), { magic: true, spell: true });
        if (u.human || t.human) this.dmgEvents.push({ x: t.x, y: t.y - 50, text: '恃才', team: t.team, src: u.id, tgt: t.id });
      }
    }),
    // 解謎：輪流亮牌，逾時自動鎖定；電腦等到想要的那張就鎖。鎖定的牌 8 秒內沒打出就作廢
    hook('step.hero', 30, function (u, dt) {
      if (u.cardSel) {
        const el = this.t - u.cardSel.t0;
        if (el >= 4) this.lockCard(u);
        else if (!u.human && el > 0.3) {
          const tg = u.ai && u.ai.target, want = u.mp < u.maxMp * 0.3 ? 0 : tg && tg.kind === 'hero' ? 2 : 1;
          if (cardIdx(this, u) === want) {
            this.lockCard(u);
            const i = u.spec.skills.findIndex((s) => s.x && s.x.k === 'card'); // 鎖定後進冷卻（真人是按第二次時由 tryCast 處理）
            if (u.rc && u.rc[i]) { u.rc[i] = null; u.cd[i] = this.skillCd(u, u.spec.skills[i], i, u.skillLv[i]); }
          }
        }
      }
      if (u.card && u.card.until < this.t) u.card = null;
    }),
    hook('revive', 40, function (u) { u.cardSel = null; u.card = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    card: ({ w, u, t, d, hero, hpR, foeHeroes }) => !!t && d < u.range + 80 && !u.card && !u.cardSel,
    destiny: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 900 && !(u.destinyOpen > w.t),
  },
});
